import React from "react";
import { Button, Menu } from "@jugl-web/ui-kit";
import {
  InvoiceStatus,
  InvoiceType,
} from "@jugl-web/rest-api/invoices/models/common-types";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import { InvoiceModel } from "@jugl-web/rest-api";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import {
  InvoicesTableActions,
  InvoicesTableNavigateFn,
  InvoicesAvailableAction,
} from "@jugl-web/domain-resources/invoices/types";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";
import { ReactComponent as LinkIcon } from "./assets/link.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { useChangeInvoiceTypeOptions } from "../../../InvoicesTable/hooks/useChangeInvoiceTypeOptions";

interface ComponentProps {
  className?: string;
  onPrint: () => void;
  onCopyUrl: () => Promise<void>;
  invoiceId?: string;
  status: InvoiceStatus | undefined;
  type: InvoiceType | undefined;
  onActionSuccess?: (
    action: "edit" | "void-invoice" | "convert",
    invoice?: InvoiceModel
  ) => void;
  displayPlace?: "table" | "item-form";
  isReadOnly?: boolean;
  actions: InvoicesTableActions;
  onNavigate?: InvoicesTableNavigateFn;
  availableActions: InvoicesAvailableAction[];
}

export const PreviewActions = ({
  className = "",
  onPrint,
  onCopyUrl,
  invoiceId,
  status,
  type,
  displayPlace = "item-form",
  onActionSuccess,
  isReadOnly = false,
  actions,
  onNavigate,
  availableActions,
}: ComponentProps) => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });

  const dict = useInvoiceTypeToLabelDictionary();

  const options = useChangeInvoiceTypeOptions({
    type: type || "invoice",
  });

  const isVoided = status === "void";

  const isTableView = displayPlace === "table";

  const menuSections = [
    [
      ...(isReadOnly
        ? []
        : [
            ...(isTableView
              ? [
                  {
                    id: "edit",
                    label: t({
                      id: "invoices.edit",
                      defaultMessage: "Edit",
                    }),
                    icon: <PenIcon />,
                    isHidden: !availableActions.includes("update"),
                  },
                ]
              : []),
            ...(isTableView ? options : []),
          ]),
      {
        id: "download-pdf",
        label: t({
          id: "invoices.download-pdf",
          defaultMessage: "Download PDF",
        }),
        icon: <DownloadIcon />,
        isHidden: !availableActions.includes("download-pdf"),
      },
      {
        id: "copy-url",
        label: t({
          id: "invoices.copy-url",
          defaultMessage: "Copy URL",
        }),
        icon: <LinkIcon />,
        isHidden: !invoiceId,
        isDisabled: !availableActions.includes("copy-url"),
      },
      ...(isReadOnly
        ? []
        : [
            {
              id: "void-invoice",
              label: `${t({
                id: "invoices.void",
                defaultMessage: "Void",
              })} ${dict[type || "invoice"]}`,
              icon: <TrashIcon />,
              className: "border-0 border-t border-grey-200 border-solid",
              isDisabled: isVoided,
              isHidden: !invoiceId || !availableActions.includes("update"),
            },
          ]),
    ],
  ];

  const handleMenuSelect = async (
    selected: { id: string },
    _event: React.MouseEvent<HTMLButtonElement>,
    close: () => void
  ) => {
    switch (selected.id) {
      case "edit":
        if (invoiceId && onNavigate) {
          onNavigate("invoicesItemFormEdit", {
            id: invoiceId,
          });
        }
        break;
      case "download-pdf":
        onPrint();
        break;
      case "copy-url":
        await onCopyUrl();
        close();
        break;
      case "void-invoice": {
        if (!invoiceId || !actions.updateInvoice) {
          return;
        }
        await actions.updateInvoice(invoiceId, {
          status: "void",
        });
        onActionSuccess?.(selected.id);
        toast(
          t(
            {
              id: "invoices.invoice-voided",
              defaultMessage: "{type} voided",
            },
            {
              type: dict[type || "invoice"],
            }
          ),
          { variant: "success" }
        );
        onNavigate?.("invoices");
        close();
        break;
      }
      case "convert-to-proforma":
      case "convert-to-invoice":
      case "convert-to-purchase-order": {
        if (!invoiceId) {
          return;
        }
        actions.changeInvoiceType?.(selected.id, invoiceId).then((invoice) => {
          onActionSuccess?.("convert", invoice);
        });
        close();
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      className={cx(
        "flex shrink-0 items-center gap-4 px-[70px] py-6",
        className
      )}
    >
      <Menu
        placement="top-start"
        sections={menuSections}
        onSelect={handleMenuSelect}
        className="max-h-[500px]"
        renderTrigger={({ Trigger, triggerRef }) => (
          <Trigger
            ref={triggerRef}
            as={Button}
            className="relative h-10 w-28 pr-10"
            iconEnd={
              <div className="absolute right-0 my-2 h-2/3 w-10 pr-0.5 text-white">
                <ChevronIcon />
              </div>
            }
          >
            {t({
              id: "invoices.actions",
              defaultMessage: "Actions",
            })}
          </Trigger>
        )}
      />
    </div>
  );
};
